"use client"

import { useHydrated } from "@immi/ui/hooks/use-hydrated"

import { relativeTime } from "@/lib/utils"

interface RelativeTimeProps {
  /** ISO string ou null. */
  date: string | null
  /** Texto exibido no servidor / antes da hidratação. */
  placeholder?: string
}

/**
 * Tempo relativo ("há 3h") renderizado só no cliente. No servidor mostra um
 * placeholder estável — evita mismatch de hidratação (#418), já que
 * relativeTime() usa Date.now(). Renderiza texto puro (sem wrapper) via
 * useHydrated (useSyncExternalStore), então não precisa de
 * suppressHydrationWarning.
 */
export function RelativeTime({ date, placeholder = "" }: RelativeTimeProps) {
  const hydrated = useHydrated()
  return <>{hydrated ? relativeTime(date) : placeholder}</>
}

interface LocalDateProps {
  /** ISO string, Date ou timestamp. */
  date: string | number | Date
  /** Opções passadas pra toLocaleDateString/toLocaleString. */
  options?: Intl.DateTimeFormatOptions
  /** Inclui hora (usa toLocaleString em vez de toLocaleDateString). */
  withTime?: boolean
  locale?: string
  placeholder?: string
}

/**
 * Data/hora formatada por locale renderizada só no cliente. Formatação de
 * locale/timezone diverge entre servidor (UTC) e cliente → placeholder estável
 * no SSR evita o mismatch (#418).
 */
export function LocalDate({
  date,
  options,
  withTime = false,
  locale = "pt-BR",
  placeholder = "",
}: LocalDateProps) {
  const hydrated = useHydrated()
  if (!hydrated) return <>{placeholder}</>
  const d = date instanceof Date ? date : new Date(date)
  const formatted = withTime
    ? d.toLocaleString(locale, options)
    : d.toLocaleDateString(locale, options)
  return <>{formatted}</>
}
